/**
 * Convert a chat message's raw text into React nodes with URLs turned
 * into clickable links. Used by every chat bubble (DM, ritual group, AI)
 * so a shared ritual / profile URL renders as a link instead of
 * uncopyable plain text.
 *
 * Two-tier link routing:
 *   - URLs that point at the app's own origin (compared against
 *     env.VITE_APP_URL) become client-side `<Link>` navigations so the
 *     user doesn't lose chat state to a full reload.
 *   - Everything else becomes a plain `<a target="_blank">` with
 *     noopener / noreferrer.
 *
 * Plain-text segments come through unchanged; the consumer wraps the
 * whole thing in `whitespace-pre-wrap` so line breaks survive.
 */

import { Fragment } from "react"
import { Link } from "react-router-dom"
import { env } from "@/config/env"

// Match http(s) URLs. Stop at whitespace, angle/quote chars, and
// brackets/parens/braces/backticks so a URL inside `(see https://x.y)`
// or `[link](https://x.y)` doesn't swallow the wrapping punctuation.
// We still post-trim trailing sentence punctuation in case the input
// has e.g. "...check https://x.y," with no wrapping bracket.
//
// Trade-off: URLs that legitimately contain `(` `)` (Wikipedia
// disambiguation pages etc.) get truncated. That's much rarer in chat
// than parenthetical asides, so we accept the corner case.
const URL_REGEX = /\bhttps?:\/\/[^\s<>"'(){}[\]`]+/gi

// Trailing characters that almost always belong to the surrounding
// sentence, not the URL itself. Mirrors the heuristic used by most
// linkify libraries (linkifyjs etc) without taking the dep.
const TRAILING_TRIM = /[.,;:!?)\]}'"`]+$/

// Cached origin for the app, derived from the validated env var. Falls
// back gracefully if VITE_APP_URL is malformed (zod already validates
// it as a URL at boot, but parse defensively in case env evolves).
const APP_ORIGIN: string | null = (() => {
  try {
    return new URL(env.VITE_APP_URL).origin
  } catch {
    return null
  }
})()

interface ParsedUrl {
  href: string
  display: string
  trailing: string
  internalPath: string | null
}

const parseUrl = (raw: string): ParsedUrl => {
  const trailingMatch = raw.match(TRAILING_TRIM)
  const trailing = trailingMatch?.[0] ?? ""
  const href = trailing ? raw.slice(0, -trailing.length) : raw

  let internalPath: string | null = null
  if (APP_ORIGIN) {
    try {
      const parsed = new URL(href)
      if (parsed.origin === APP_ORIGIN) {
        internalPath = `${parsed.pathname}${parsed.search}${parsed.hash}`
      }
    } catch {
      // Malformed URL string ended up in a message; render as text.
    }
  }

  return { href, display: href, trailing, internalPath }
}

export function renderMessageContent(content: string): React.ReactNode {
  // Fast path: no protocol in the string -> nothing to do.
  if (!content.includes("http")) return content

  const out: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Reset lastIndex because URL_REGEX is module-scoped /g.
  URL_REGEX.lastIndex = 0

  // biome-ignore lint/suspicious/noAssignInExpressions: idiomatic exec loop
  while ((match = URL_REGEX.exec(content)) !== null) {
    const start = match.index
    if (start > lastIndex) {
      out.push(content.slice(lastIndex, start))
    }

    const { href, display, trailing, internalPath } = parseUrl(match[0])
    const key = `link-${start}`

    if (internalPath) {
      out.push(
        <Link key={key} to={internalPath} className="underline underline-offset-2 hover:opacity-80">
          {display}
        </Link>,
      )
    } else {
      out.push(
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:opacity-80"
        >
          {display}
        </a>,
      )
    }

    if (trailing) {
      out.push(<Fragment key={`${key}-tail`}>{trailing}</Fragment>)
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    out.push(content.slice(lastIndex))
  }

  return out
}
